import { Children, isValidElement } from "react";
// TODO: This module should be ElementChildren, and should use named exports.

/**
 * Iterates through children that are typically specified as `props.children`,
 * but only maps over children that are "valid components".
 *
 * The mapFunction provided index will be normalised to the components mapped,
 * so an invalid component would not increase the index.
 *
 * @param {?*} children Children tree container.
 * @param {function(*, int)} func.
 * @param {*} context Context for func.
 * @return {object} Object containing the ordered map of results.
 */
function map(children: any, func: any, context: any) {
  let index = 0;

  return Children.map(children, (child: any) => {
    if (!isValidElement(child)) {
      return child;
    }

    return func.call(context, child, index++);
  });
}

/**
 * Iterates through children that are "valid components".
 *
 * The provided forEachFunc(child, index) will be called for each
 * leaf child with the index reflecting the position relative to "valid components".
 *
 * @param {?*} children Children tree container.
 * @param {function(*, int)} func.
 * @param {*} context Context for context.
 */
function forEach(children: any, func: any, context: any) {
  let index = 0;

  Children.forEach(children, (child: any) => {
    if (!isValidElement(child)) {
      return;
    }

    func.call(context, child, index++);
  });
}

/**
 * Count the number of "valid components" in the Children container.
 *
 * @param {?*} children Children tree container.
 * @returns {number}
 */
function count(children: any) {
  let result = 0;

  Children.forEach(children, (child: any) => {
    if (!isValidElement(child)) {
      return;
    }

    ++result;
  });

  return result;
}

/**
 * Finds children that are typically specified as `props.children`,
 * but only iterates over children that are "valid components".
 *
 * The provided forEachFunc(child, index) will be called for each
 * leaf child with the index reflecting the position relative to "valid components".
 *
 * @param {?*} children Children tree container.
 * @param {function(*, int)} func.
 * @param {*} context Context for func.
 * @returns {array} of children that meet the func return statement
 */
function filter(children: any, func: any, context: any) {
  let index = 0;
  const result: any = [];

  Children.forEach(children, (child: any) => {
    if (!isValidElement(child)) {
      return;
    }

    if (func.call(context, child, index++)) {
      result.push(child);
    }
  });

  return result;
}

function find(children: any, func: any, context: any) {
  let index = 0;
  let result: any;

  Children.forEach(children, (child: any) => {
    if (result) {
      return;
    }
    if (!isValidElement(child)) {
      return;
    }

    if (func.call(context, child, index++)) {
      result = child;
    }
  });

  return result;
}

function every(children: any, func: any, context: any) {
  let index = 0;
  let result = true;

  Children.forEach(children, (child: any) => {
    if (!result) {
      return;
    }
    if (!isValidElement(child)) {
      return;
    }

    if (!func.call(context, child, index++)) {
      result = false;
    }
  });

  return result;
}

function some(children: any, func: any, context: any) {
  let index = 0;
  let result = false;

  Children.forEach(children, (child: any) => {
    if (result) {
      return;
    }
    if (!isValidElement(child)) {
      return;
    }

    if (func.call(context, child, index++)) {
      result = true;
    }
  });

  return result;
}

function toArray(children: any) {
  const result: any = [];

  Children.forEach(children, (child: any) => {
    if (!isValidElement(child)) {
      return;
    }

    result.push(child);
  });

  return result;
}

export default {
  map,
  forEach,
  count,
  find,
  filter,
  every,
  some,
  toArray,
};
